/**
 * Whether a section leader's section time in one course clashes with
 * their sections in other courses.
 *
 * Time indexes are course-relative, so a clash is judged in real time
 * from each course's `firstSectionTimestamp`. That field may be stored
 * without a UTC offset, and the existing helpers read such a value
 * differently (`computeSectionInstant` in the runtime zone,
 * `parseFirstSectionTimestamp` as UTC-7), so this check accepts only
 * anchors with an explicit offset ({@link parseSectionAnchorMs}); every
 * parser agrees on those.
 */
import {z} from 'zod';

import {ONE_DAY_MS, ONE_MINUTE_MS, ONE_WEEK_MS} from '../utils/time';
import {parseFirstSectionTimestamp} from './courseWeekApi';
import {SECTION_MEETING_MINUTES} from './sectionApi';
import {computeSectionInstant, getSectionClock} from './slotTime';

/**
 * Smallest break, in minutes, a section leader must have between the end
 * of one course's section meeting and the start of another's.
 */
export const SL_CROSS_COURSE_MIN_GAP_MINUTES = 10;

/**
 * Course length assumed when a course has no `nWeeks`, used only to decide
 * whether that course could overlap another at all.
 */
export const CROSS_COURSE_ASSUMED_MAX_WEEKS = 16;

/**
 * Margin added to both ends of a course's span when deciding whether two
 * courses could overlap: covers slots offset from the anchor by up to two
 * days and an unknown UTC offset on an offset-less anchor.
 */
const COURSE_SPAN_MARGIN_MS = 3 * ONE_DAY_MS;

/** ISO 8601 date-time with an explicit `Z` or `±hh:mm` offset. */
const OFFSET_TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Parses a course's `firstSectionTimestamp` for the cross-course check,
 * accepting only an ISO 8601 date-time with an explicit `Z` or `±hh:mm`
 * offset.
 *
 * @param raw - The stored value.
 * @return The anchor in epoch ms, or `undefined` when `raw` is not a
 *   string, has no offset, or is not a valid instant.
 * @throws Never.
 */
export function parseSectionAnchorMs(raw: unknown): number | undefined {
  if (typeof raw !== 'string' || !OFFSET_TIMESTAMP_PATTERN.test(raw)) {
    return undefined;
  }
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? undefined : ms;
}

/**
 * The course fields that place a course's section meetings in time. A
 * course whose anchor lacks an explicit offset, or that has no positive
 * integer `nWeeks`, fails to parse.
 */
export const SectionCourseScheduleSchema = z.object({
  /**
   * `course.firstSectionTimestamp`: the instant of slot 0 in week 0,
   * accepted only by {@link parseSectionAnchorMs}.
   */
  firstSectionTimestamp: z
    .string()
    .refine(value => parseSectionAnchorMs(value) !== undefined),
  /** `course.nWeeks`: sections meet once in each of weeks `[0, nWeeks)`. */
  nWeeks: z.number().int().positive(),
  /**
   * `course.timezone`, read with `getSectionClock`. A malformed value reads
   * as absent rather than failing the schedule.
   */
  timezone: z.string().optional().catch(undefined),
  /** `course.sectionTimesFollowCourseTimezone`; malformed reads as absent. */
  sectionTimesFollowCourseTimezone: z.boolean().optional().catch(undefined),
});

/** See {@link SectionCourseScheduleSchema}. */
export type SectionCourseSchedule = z.infer<typeof SectionCourseScheduleSchema>;

/** One section's weekly meetings. */
export interface SectionMeetingSeries {
  /** The section's course schedule. */
  schedule: SectionCourseSchedule;
  /** The section's slot; `'none'` has no meetings. */
  timeIndex: number | 'none';
  /**
   * Length of each meeting in minutes. When omitted,
   * {@link SECTION_MEETING_MINUTES}.
   */
  meetingMinutes?: number;
}

/**
 * Whether any meeting of `a` comes within `minGapMinutes` of any meeting
 * of `b`. Each meeting is the half-open interval `[start, start + length)`
 * in epoch ms, with `start` from `computeSectionInstant` on the course's
 * section clock for weeks `[0, nWeeks)`. Two meetings conflict when they overlap or when the break
 * between the end of the earlier one and the start of the later one is
 * shorter than `minGapMinutes`; a break of exactly `minGapMinutes` is
 * allowed. For equal lengths that is `|startA − startB| < length + gap`.
 *
 * @param params.a - One section's meetings.
 * @param params.b - The other section's meetings.
 * @param params.minGapMinutes - Required break. When omitted,
 *   {@link SL_CROSS_COURSE_MIN_GAP_MINUTES}.
 * @return `false` when either side is `'none'`, otherwise whether any pair
 *   of meetings conflicts.
 * @throws Never for schedules that passed {@link SectionCourseScheduleSchema}.
 */
export function doSectionMeetingSeriesConflict({
  a,
  b,
  minGapMinutes = SL_CROSS_COURSE_MIN_GAP_MINUTES,
}: {
  a: SectionMeetingSeries;
  b: SectionMeetingSeries;
  minGapMinutes?: number;
}): boolean {
  const startsA = meetingStartsMs(a);
  const startsB = meetingStartsMs(b);
  const gapMs = minGapMinutes * ONE_MINUTE_MS;
  const lengthAMs =
    (a.meetingMinutes ?? SECTION_MEETING_MINUTES) * ONE_MINUTE_MS;
  const lengthBMs =
    (b.meetingMinutes ?? SECTION_MEETING_MINUTES) * ONE_MINUTE_MS;
  return startsA.some(startA =>
    startsB.some(
      startB =>
        startB - startA < lengthAMs + gapMs &&
        startA - startB < lengthBMs + gapMs,
    ),
  );
}

/** A section leader's placed section in another course. */
export interface OtherCourseSection {
  /** The other course. */
  courseId: string;
  /** The section's slot in that course. */
  timeIndex: number;
  /** The other course's document data, unvalidated. */
  course: unknown;
}

/** Outcome of {@link checkCrossCourseSectionTime}. */
export type CrossCourseSectionCheck<T extends OtherCourseSection> =
  | {
      /** No other section clashes. */
      kind: 'clear';
    }
  | {
      /** `section` clashes with the requested slot. */
      kind: 'conflict';
      /** The first clashing section. */
      section: T;
    }
  | {
      /**
       * A course that could overlap has no usable schedule (see
       * {@link SectionCourseScheduleSchema}), so the clash cannot be judged.
       */
      kind: 'anchor-invalid';
      /** The course with the unusable schedule. */
      courseId: string;
    };

/**
 * Checks a section at `timeIndex` in `courseId` against the section
 * leader's sections in other courses.
 *
 * Other courses whose span cannot overlap this course's are skipped
 * first, even when their schedule is unusable, so a section led in a past
 * course never blocks a pick. A course's span is the closed interval
 * `[anchor − margin, anchor + weeks + margin]`, with the anchor read
 * leniently by `parseFirstSectionTimestamp`, `nWeeks` defaulting to
 * {@link CROSS_COURSE_ASSUMED_MAX_WEEKS}, and a margin of three days; a
 * course whose anchor cannot be read at all always counts as overlapping.
 * For the remaining courses, both schedules must pass
 * {@link SectionCourseScheduleSchema} and the clash is
 * {@link doSectionMeetingSeriesConflict}.
 *
 * @param params.courseId - The course being picked in.
 * @param params.course - That course's document data, unvalidated.
 * @param params.timeIndex - The slot requested.
 * @param params.others - The section leader's placed sections in other
 *   courses; any in `courseId` are ignored.
 * @return `'clear'`, the first clashing section, or the first course
 *   (this one before any other) whose schedule is unusable.
 * @throws Never.
 */
export function checkCrossCourseSectionTime<T extends OtherCourseSection>({
  courseId,
  course,
  timeIndex,
  others,
}: {
  courseId: string;
  course: unknown;
  timeIndex: number;
  others: readonly T[];
}): CrossCourseSectionCheck<T> {
  const relevant = others.filter(
    other =>
      other.courseId !== courseId &&
      mayCoursesOverlap({a: course, b: other.course}),
  );
  if (relevant.length === 0) {
    return {kind: 'clear'};
  }
  const schedule = SectionCourseScheduleSchema.safeParse(course);
  if (!schedule.success) {
    return {kind: 'anchor-invalid', courseId};
  }
  for (const other of relevant) {
    const otherSchedule = SectionCourseScheduleSchema.safeParse(other.course);
    if (!otherSchedule.success) {
      return {kind: 'anchor-invalid', courseId: other.courseId};
    }
    if (
      doSectionMeetingSeriesConflict({
        a: {schedule: schedule.data, timeIndex},
        b: {schedule: otherSchedule.data, timeIndex: other.timeIndex},
      })
    ) {
      return {kind: 'conflict', section: other};
    }
  }
  return {kind: 'clear'};
}

/** The lenient fields read by {@link mayCoursesOverlap}. */
const LenientCourseSpanSchema = z.object({
  firstSectionTimestamp: z.string().optional().catch(undefined),
  nWeeks: z.number().int().positive().optional().catch(undefined),
});

function courseSpanMs(course: unknown): [number, number] | undefined {
  const parsed = LenientCourseSpanSchema.safeParse(course);
  if (!parsed.success || parsed.data.firstSectionTimestamp === undefined) {
    return undefined;
  }
  const anchor = parseFirstSectionTimestamp(
    parsed.data.firstSectionTimestamp,
  )?.getTime();
  if (anchor === undefined) {
    return undefined;
  }
  const weeks = parsed.data.nWeeks ?? CROSS_COURSE_ASSUMED_MAX_WEEKS;
  return [
    anchor - COURSE_SPAN_MARGIN_MS,
    anchor + weeks * ONE_WEEK_MS + COURSE_SPAN_MARGIN_MS,
  ];
}

function mayCoursesOverlap({a, b}: {a: unknown; b: unknown}): boolean {
  const spanA = courseSpanMs(a);
  const spanB = courseSpanMs(b);
  if (spanA === undefined || spanB === undefined) {
    return true;
  }
  return spanA[0] <= spanB[1] && spanB[0] <= spanA[1];
}

function meetingStartsMs({
  schedule,
  timeIndex,
}: SectionMeetingSeries): number[] {
  if (timeIndex === 'none') {
    return [];
  }
  return Array.from({length: schedule.nWeeks}, (_, weekIndex) =>
    computeSectionInstant({
      firstSectionTimestamp: schedule.firstSectionTimestamp,
      timeIndex,
      weekIndex,
      ...getSectionClock(schedule),
    }).getTime(),
  );
}
